'use client';

import React, { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { NinetailedProvider, useNinetailed } from '@ninetailed/experience.js-react';
import { getNt, setPreviewMode } from 'helpers/utils/ninetailed';

/**
 * Tracks App Router route changes and calls ninetailed.page() so the SDK
 * registers each navigation as a new page-view event — exactly like the
 * `await nt.page()` call in the POC's main() function.
 */
const RouteTracker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pathname = usePathname();
  const ninetailed = useNinetailed();

  useEffect(() => {
    // Fire a page() event on every navigation, including the initial page load.
    // NinetailedProvider does NOT auto-call page() — without this the SDK never
    // fetches a profile and onSelectVariant stays stuck in 'loading' forever.
    void ninetailed.page().catch((err) => console.error('[Ninetailed] page() failed:', err));
  }, [pathname, ninetailed]);

  return <>{children}</>;
};

/**
 * Wraps the app with NinetailedProvider so every <Experience> / useExperience
 * call in the tree has SDK context.
 *
 * Passes the getNt() singleton via the `ninetailed` prop so that
 * NinetailedProvider, RouteTracker, and getNinetailedComponent all share the
 * exact same Ninetailed instance — and therefore the same anonymousId, profile
 * state, and event queue. Without this, the provider creates its own separate
 * instance and onSelectVariant never sees the page() events fired by the provider.
 *
 * Requires NEXT_PUBLIC_NINETAILED_CLIENT_ID in .env.
 * Falls back to rendering children as-is when the env var is missing.
 */
const PersonalizationProvider: React.FC<{ children: React.ReactNode; isPreview?: boolean }> = ({
  children,
  isPreview = false,
}) => {
  const clientId = process.env.NEXT_PUBLIC_NINETAILED_CLIENT_ID;

  if (!clientId) {
    return <>{children}</>;
  }

  // Propagate the server-resolved preview flag to the ninetailed helper so preview-only behavior can be gated correctly.
  setPreviewMode(isPreview);

  return (
    <NinetailedProvider ninetailed={getNt()}>
      <RouteTracker>{children}</RouteTracker>
    </NinetailedProvider>
  );
};

export default PersonalizationProvider;
